import { useState } from 'react';
import { API_HOST } from '../../utils/api';
import { exportBannersAsPng } from '../../utils/bannerPng';
import { useToast } from './Toast';
import {
  HiOutlinePrinter, HiOutlinePhotograph, HiOutlinePresentationChartBar,
  HiOutlineDocumentText, HiOutlineCode, HiOutlineExclamation, HiOutlineCheckCircle, HiOutlinePencil,
} from 'react-icons/hi';

const parseReport = (r) => {
  if (!r) return null;
  if (typeof r === 'object') return r;
  try { return JSON.parse(r); } catch { return null; }
};

/** Results of the automatic post-generation checks (server assetValidator). */
export function QualityReport({ report }) {
  const q = parseReport(report);
  if (!q) return null;
  const placeholders = q.placeholders || [];
  const warnings = q.warnings || [];

  if (!placeholders.length && !warnings.length) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
        <HiOutlineCheckCircle className="w-5 h-5 flex-shrink-0" /> Passed our quality checks.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {placeholders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-900 mb-1.5">
            <HiOutlinePencil className="w-4 h-4" />
            {placeholders.length} {placeholders.length === 1 ? 'item' : 'items'} to fill in before you use this
          </div>
          <p className="text-xs text-blue-800 mb-2">We never make up facts about your business. Look for <code className="bg-white px-1 rounded">[ADD: …]</code> in the asset:</p>
          <ul className="text-sm text-blue-900 list-disc pl-5 space-y-0.5">
            {placeholders.slice(0, 8).map(p => <li key={p}>{p}</li>)}
            {placeholders.length > 8 && <li>…and {placeholders.length - 8} more</li>}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-900 mb-1.5">
            <HiOutlineExclamation className="w-4 h-4" /> Worth a look before publishing
          </div>
          <ul className="text-sm text-amber-900 list-disc pl-5 space-y-0.5">
            {warnings.map(w => <li key={w.code}>{w.message}</li>)}
          </ul>
          <p className="text-xs text-amber-800 mt-2">Use "Request Changes" to have these fixed automatically.</p>
        </div>
      )}
    </div>
  );
}

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50';

/**
 * Extra download formats for partner assets.
 * - one_pager: Save as PDF (prints the preview iframe via onPrint)
 * - banner: PNG images (rendered in the browser)
 * - presentation: PowerPoint
 * - service_page: page copy (.docx) + embed snippet (.html)
 */
export function ExportActions({ assetType, assetId, previewUrl, onPrint, baseName }) {
  const [busy, setBusy] = useState(false);
  const { addToast } = useToast();
  const exportUrl = (kind) => `${API_HOST}/api/assets/${assetId}/export/${kind}`;

  const handlePng = async () => {
    setBusy(true);
    try {
      const n = await exportBannersAsPng(previewUrl, baseName || 'banner');
      addToast(`Downloaded ${n} PNG${n === 1 ? '' : 's'}`);
    } catch (err) {
      addToast(err.message || 'PNG export failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (assetType === 'one_pager' && onPrint) {
    return (
      <button onClick={onPrint} className={btn} title="Opens your browser's print dialog. Choose 'Save as PDF'.">
        <HiOutlinePrinter className="w-4 h-4" /> Save as PDF
      </button>
    );
  }
  if (assetType === 'banner') {
    return (
      <button onClick={handlePng} disabled={busy} className={btn} title="Download each banner size as a PNG image for ad platforms and social posts">
        <HiOutlinePhotograph className="w-4 h-4" /> {busy ? 'Creating PNGs…' : 'Download PNGs'}
      </button>
    );
  }
  if (assetType === 'presentation') {
    return (
      <a href={exportUrl('pptx')} className={btn} title="Editable PowerPoint with speaker notes">
        <HiOutlinePresentationChartBar className="w-4 h-4" /> PowerPoint
      </a>
    );
  }
  if (assetType === 'service_page') {
    return (
      <>
        <a href={exportUrl('copy')} className={btn} title="Just the words, section by section, to paste into your website editor">
          <HiOutlineDocumentText className="w-4 h-4" /> Page copy (.docx)
        </a>
        <a href={exportUrl('embed')} className={btn} title="Paste into a Custom HTML block in WordPress, Wix, or Squarespace">
          <HiOutlineCode className="w-4 h-4" /> Embed snippet
        </a>
      </>
    );
  }
  return null;
}
